import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import api from '../services/api';

interface ContractorAccount {
  _id: string;
  email: string;
  name: string;
  phone?: string;
  hasSubscription: boolean;
  subscriptionStatus?: 'active' | 'expired' | 'cancelled' | 'free';
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  totalJobsAssigned: number;
  totalQuoteInvitations: number;
  totalActionCount: number;
  totalQuoteViews: number;
  firstJobDate?: string;
  lastActionDate?: string;
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const AdminContractors = () => {
  const [contractors, setContractors] = useState<ContractorAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Pagination and search
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<ContractorAccount | null>(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    hasSubscription: false,
    subscriptionStatus: 'active' as 'active' | 'expired' | 'cancelled',
    subscriptionStartDate: '',
    subscriptionEndDate: ''
  });

  useEffect(() => {
    fetchContractors();
  }, [page, rowsPerPage, search]);

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/contractors', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search
        }
      });
      
      setContractors(response.data.contractors);
      setTotal(response.data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch contractors');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (contractor: ContractorAccount) => {
    setSelectedContractor(contractor);
    setEditForm({
      hasSubscription: contractor.hasSubscription,
      subscriptionStatus: contractor.subscriptionStatus || 'active',
      subscriptionStartDate: contractor.subscriptionStartDate ? 
        new Date(contractor.subscriptionStartDate).toISOString().split('T')[0] : '',
      subscriptionEndDate: contractor.subscriptionEndDate ? 
        new Date(contractor.subscriptionEndDate).toISOString().split('T')[0] : ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedContractor) return;
    
    try {
      await api.put(`/admin/contractors/${selectedContractor._id}/subscription`, editForm);
      setSuccess('Contractor subscription updated successfully');
      setEditDialogOpen(false);
      fetchContractors();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update contractor');
    }
  };

  const handleDelete = (contractor: ContractorAccount) => {
    setSelectedContractor(contractor);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedContractor) return;
    
    try {
      await api.delete(`/admin/contractors/${selectedContractor._id}`);
      setSuccess('Contractor account deleted successfully');
      setDeleteDialogOpen(false);
      fetchContractors();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete contractor');
    }
  };

  const handleReset = (contractor: ContractorAccount) => {
    setSelectedContractor(contractor);
    setResetDialogOpen(true);
  };

  const handleConfirmReset = async () => {
    if (!selectedContractor) return;
    
    try {
      await api.post(`/admin/contractors/${selectedContractor._id}/reset`);
      setSuccess('Contractor account reset successfully');
      setResetDialogOpen(false);
      fetchContractors();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset contractor');
    }
  };

  const handleResetMonthlyViews = async (contractor: ContractorAccount) => {
    try {
      await api.post(`/admin/contractors/${contractor._id}/reset-monthly-views`);
      setSuccess('Total quote views reset successfully');
      fetchContractors();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset quote views');
    }
  };

  const getSubscriptionChip = (contractor: ContractorAccount) => {
    if (contractor.hasSubscription) {
      const status = contractor.subscriptionStatus || 'active';
      const color = status === 'active' ? 'success' : status === 'expired' ? 'warning' : 'error';
      return <Chip label={status} color={color} size="small" />;
    }
    return <Chip label="Free" color="default" size="small" />;
  };

  if (loading && contractors.length === 0) {
    return (
      <Container>
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading contractors...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Contractor Account Management
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Search by email, name, or phone..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchContractors}
                disabled={loading}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Contractor</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Total Quote Views</TableCell>
                <TableCell>Total Actions</TableCell>
                <TableCell>Jobs Assigned</TableCell>
                <TableCell>Last Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contractors.map((contractor) => (
                <TableRow key={contractor._id}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {contractor.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contractor.email}
                      </Typography>
                      {contractor.phone && (
                        <Typography variant="caption" color="text.secondary">
                          {contractor.phone}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getSubscriptionChip(contractor)}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {contractor.totalQuoteViews || 0}/3
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(contractor.totalQuoteViews || 0) >= 3 ? 'Limit reached' : `${3 - (contractor.totalQuoteViews || 0)} remaining`}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {contractor.totalActionCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {contractor.totalJobsAssigned}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {contractor.lastActionDate ? 
                        new Date(contractor.lastActionDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 
                        'Never'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit Subscription">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(contractor)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Account">
                        <IconButton
                          size="small"
                          onClick={() => handleReset(contractor)}
                        >
                          <RestoreIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Total Quote Views">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleResetMonthlyViews(contractor)}
                            disabled={(contractor.totalQuoteViews || 0) === 0}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete Account">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(contractor)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Contractor Subscription</DialogTitle>
        <DialogContent>
          {selectedContractor && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">{selectedContractor.name}</Typography>
              <Typography variant="body2" color="text.secondary">{selectedContractor.email}</Typography>
            </Box>
          )}
          
          <FormControlLabel
            control={
              <Switch
                checked={editForm.hasSubscription}
                onChange={(e) => setEditForm(prev => ({ ...prev, hasSubscription: e.target.checked }))}
              />
            }
            label="Has Active Subscription"
          />
          
          {editForm.hasSubscription && (
            <>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Subscription Status</InputLabel>
                <Select
                  value={editForm.subscriptionStatus}
                  onChange={(e) => setEditForm(prev => ({ ...prev, subscriptionStatus: e.target.value as any }))}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Subscription Start Date"
                type="date"
                value={editForm.subscriptionStartDate}
                onChange={(e) => setEditForm(prev => ({ ...prev, subscriptionStartDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
              />
              
              <TextField
                fullWidth
                label="Subscription End Date"
                type="date"
                value={editForm.subscriptionEndDate}
                onChange={(e) => setEditForm(prev => ({ ...prev, subscriptionEndDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Contractor Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the account for <strong>{selectedContractor?.name}</strong>? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Contractor Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset the account for <strong>{selectedContractor?.name}</strong>? 
            This will reset their quote view count, action count, and other usage counters to zero.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmReset} variant="contained">Reset</Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminContractors;
